import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import type { Category } from '@domain/finance'
import type { CategoryRepository, NewCategory } from '@domain/ports/finance-repository'
import {
  booleanOr,
  requireInteger,
  requireIso,
  requireOneOf,
  textList,
  textOr,
  toIso,
} from './converters'
import { HOUSEHOLD_ID } from './household-repository'

/**
 * A árvore de categorias: listar, criar, renomear, arquivar.
 *
 * ## O que este arquivo existe para não escrever
 *
 * **`path` e `depth` não entram no `create`.** As regras do Firestore negam os
 * dois ao cliente **inclusive na criação** — `untouched()` reprova a mera
 * presença deles num documento novo, e é o que impede uma categoria de nascer
 * já com o caminho que só a árvore inteira sabe calcular. Quem os preenche é a
 * Cloud Function da Task 9, com Admin SDK.
 *
 * Escrevê-los aqui **não falha no tipo**: `NewCategory` sequer os tem, mas
 * nada impede alguém de "consertar" o payload acrescentando-os. Falha na
 * **regra**, em runtime, com `permission-denied` e nenhuma explicação — na tela
 * de quem estava criando "Mercado". Por isso a restrição é teste e não
 * comentário, em dois níveis: `category-repository.test.ts` inspeciona o
 * payload que sai daqui, e `tests/adapters/finance-repositories.test.ts` cria
 * uma categoria de verdade contra as regras de verdade e confere que o
 * documento gravado não tem os dois campos.
 *
 * **`isSystem` também não.** A regra do `create` exige `false`, então categoria
 * do seed é necessariamente Admin SDK — e nada se perde, porque o seed precisa
 * de `path`, que este mesmo `create` proíbe.
 *
 * ## O que este arquivo NÃO cobra, e quem cobra
 *
 * - **Colisão de slug entre irmãs.** Quem cobra é a Function que cunha o `path`,
 *   na mesma transação, com duas travas: a recusa de `rewritePathsOnMove` sobre
 *   a árvore, e um documento de unicidade em id determinístico. Chamar
 *   `wouldCollideWithSibling` aqui daria a sensação de porta fechada e não a
 *   porta: a concorrência otimista do Firestore vale sobre os documentos que as
 *   leituras devolveram, e não tranca a consulta por irmãs. O lugar dele é o
 *   formulário da Task 11, para a pessoa saber antes de gravar.
 * - **Herança de `kind` do pai.** Mesma razão: quem lê a árvore para calcular
 *   `path` é a Function, e é lá que a divergência tem como ser recusada.
 */

const KINDS = ['expense', 'income', 'transfer'] as const

function categoriesPath(): string {
  return `households/${HOUSEHOLD_ID}/categories`
}

/**
 * Firestore → domínio.
 *
 * **`path` ausente vira `[]` e `depth` ausente vira `0`, e isso não é
 * tolerância decorativa:** é o estado real de toda categoria entre o `create`
 * do cliente e a passagem da Function. `categoryPath` sabe lidar com ele — a
 * spec da fase monta a árvore inteira a partir de rascunhos com `path: []` —,
 * e lançar aqui faria a tela quebrar durante o segundo em que a Function ainda
 * não rodou.
 */
export function toCategory(id: string, data: DocumentData): Category {
  const where = `categories/${id}`
  const path = textList(data['path'])
  return {
    id,
    householdId: HOUSEHOLD_ID,
    name: textOr(data['name'], 'Categoria sem nome'),
    parentId: typeof data['parentId'] === 'string' ? data['parentId'] : null,
    path,
    // `depth` é `path.length - 1` por definição (ver Docs/02-Dominio/Category.md).
    // Lido do documento quando está lá, porque é a Function que o escreve e é
    // ela a fonte; derivado do `path` quando não está, que é o mesmo instante em
    // que `path` também não está — e aí os dois são o par vazio, não um zero
    // que finge ser raiz.
    depth: data['depth'] === undefined ? Math.max(path.length - 1, 0) : requireInteger(data['depth'], `${where}.depth`),
    icon: textOr(data['icon'], 'tag'),
    color: textOr(data['color'], '#7c3aed'),
    kind: requireOneOf(data['kind'], KINDS, `${where}.kind`),
    // Mesmo default de `categoryIsSystem()` em `firestore.rules`: ausente é
    // "não é do seed". Os dois lados têm de responder a mesma coisa sobre o
    // mesmo documento, senão a tela oferece apagar o que a regra vai negar.
    isSystem: booleanOr(data['isSystem'], false),
    archivedAt: toIso(data['archivedAt']),
    createdAt: requireIso(data['createdAt'], `${where}.createdAt`),
    updatedAt: requireIso(data['updatedAt'], `${where}.updatedAt`),
  }
}

/**
 * O payload do `create` — **sem `path`, sem `depth` e sem `isSystem`**.
 *
 * Exportado para ser inspecionado: é este objeto que o teste do Passo 2 abre
 * para dizer que os dois campos negados não estão lá. Um `create` que os
 * mandasse seria recusado pela regra, e a mensagem que chegaria à tela seria
 * `permission-denied`, sem uma palavra sobre qual campo.
 */
export function categoryDocument(input: NewCategory): DocumentData {
  return {
    name: input.name,
    parentId: input.parentId,
    icon: input.icon,
    color: input.color,
    kind: input.kind,
    isSystem: false,
    archivedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }
}

/**
 * Renomear muda `name` e nada mais.
 *
 * **O slug não acompanha** (decisão 98): ele mora no `path` já gravado, e é de
 * lá que o recálculo o tira. Renomear "Mercado" para "Supermercado" mudando o
 * slug reescreveria o `path` da categoria e o de toda a descendência, e todo
 * `enrichment.categoryPath` gravado nas transações passaria a apontar para um
 * segmento que não existe mais — o histórico inteiro perdendo a categoria, por
 * uma edição de texto. O campo ainda não existe (decisão 106); a regra vale
 * desde já porque é ela que decide como ele vai ser escrito.
 *
 * Por isso o payload **não** tem `path`: nem porque a regra nega, que já
 * bastaria, mas porque não há o que escrever ali.
 */
export function renameDocument(name: string): DocumentData {
  return { name, updatedAt: serverTimestamp() }
}

/**
 * Arquivar é escrever `archivedAt`, e não apagar.
 *
 * `serverTimestamp()` e não o relógio do navegador, como em todo instante nosso.
 */
export function archiveDocument(): DocumentData {
  return { archivedAt: serverTimestamp(), updatedAt: serverTimestamp() }
}

/**
 * A árvore inteira, **arquivadas inclusive**: categoria arquivada não aparece em
 * seletor, e continua resolvendo o `categoryId` de dado antigo. Filtrar aqui
 * faria a transação de março do ano passado perder o nome da categoria dela.
 *
 * Sem `orderBy`, pelo mesmo motivo de `listAccounts` — e aqui pesa mais: a
 * ordem que interessa é a da árvore, que sai do `path`, e um `orderBy('path')`
 * deixaria de fora justamente as recém-criadas, que ainda não o têm.
 */
async function listCategories(db: Firestore): Promise<Category[]> {
  const snap = await getDocs(collection(db, categoriesPath()))
  return snap.docs.map((category) => toCategory(category.id, category.data()))
}

async function createCategory(db: Firestore, input: NewCategory): Promise<string> {
  const ref = await addDoc(collection(db, categoriesPath()), categoryDocument(input))
  return ref.id
}

async function renameCategory(db: Firestore, id: string, name: string): Promise<void> {
  await updateDoc(doc(db, categoriesPath(), id), renameDocument(name))
}

async function archiveCategory(db: Firestore, id: string): Promise<void> {
  await updateDoc(doc(db, categoriesPath(), id), archiveDocument())
}

/** A porta com o `db` já preso, para a composição em `src/main.tsx`. */
export function createFirebaseCategoryRepository(db: Firestore): CategoryRepository {
  return {
    list: () => listCategories(db),
    create: (input) => createCategory(db, input),
    rename: (id, name) => renameCategory(db, id, name),
    archive: (id) => archiveCategory(db, id),
  }
}
